import { date, digit, maxLength, maxNumber, minNumber, numeric, NumericValueType, prop, required } from "@rxweb/reactive-form-validators";
import { ProfileDetailModel } from "../profile-management/profile-detail.model";
import { AutomateDataState } from "../profile-management/profile-detail/automate-datastate-log/automate-datastate-model";
import { UserModel } from "../user-management/user-model";
import { SaleAccountViewModel } from "../sale-account-management/add-sale-acount.model";

export class SaleOpportunity {
  constructor() {
    this.saleOpportunityId = 0;
    this.probability = 0;
    this.amount = 0;
  }
  @prop()
  saleOpportunityId: number;
  @prop()
  @required()
  @maxLength({ value: 255 })
  opportunityName: string;
  @prop()
  @maxLength({ value: 255 })
  stageId: number;
  @prop()
  @maxLength({ value: 255 })
  accountName: string;
  @prop()
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numberic is allowed' })
  probability: number | 0;
  @prop()
  @maxLength({ value: 255 })
  type: string;
  @prop()
  @prop()
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numberic is allowed' })
  amount: number | 0;
  @prop()
  @maxLength({ value: 255 })
  leadSource: string;
  @prop()
  @maxLength({ value: 36 })
  referenceId: string;
  @prop()
  @maxLength({ value: 255 })
  referenceType: string;
  @prop()
  @date({ message: 'Please enter valid format: MM/DD/YYYY.Ex: 20/11/2022' })
  closeDate: Date | string | null;
  @prop()
  @maxLength({ value: 255 })
  company: string;
  dateCreated: Date | string | null;
  dateModified: Date | string | null;
  opportunityProfile: ProfileDetailModel | null;
  // fetching data:
  lostReason: string | null;
  @prop()
  @date({ message: 'Please enter valid format: MM/DD/YYYY.Ex: 20/11/2022' })
  secondaryCloseDate: Date | string | null;
  offsetTime: number;
  realEventDateTime: Date | string | null;
  currentFormatTime: string | null;
}
export class SaleOpportunityViewModel extends SaleOpportunity {
  @prop()
  saleAccountId: string;
}
export class SaleOpportunityProfile extends ProfileDetailModel {
  saleOpportunityId: number;
  saleOpportunity: SaleOpportunityViewModel;
}
export class ProposalNonArtistFee {
  proposalNonartistFeeId: number;
  proposalId: string;
  class: string;
  typeName: string;
  amount: number;
  description: string;
  deleted: boolean;
  dateCreated: Date | string | null;
  dateModified: Date | string | null;
  dateDeleted: Date | string | null;
  saleOpportunityProfile: ProfileDetailModel | null;

}
export class ProposalNonArtistFeeInputModel {
  proposalNonartistFeeId: number;
  proposalId: string;
  class: string;
  @required()
  @prop()
  typeName: string;
  @required()
  @prop()
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  amount: number;
  @prop()
  description: string;
  deleted: boolean;
  dateCreated: Date | string | null;
  dateModified: Date | string | null;
  dateDeleted: Date | string | null;
  saleOpportunityProfile: ProfileDetailModel | null;
}
export interface ProposalArtistLineItem {
  name: string;
  description?: string;
  cost: number;
  clientFee: number;
  visibleToClient: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export class ProposalArtists {
  @prop()
  id: number = 0;
  @prop()
  @required()
  profileId: string;
  @prop()
  @required()
  @numeric({ acceptValue: NumericValueType.PositiveNumber, allowDecimal: false, message: 'Only positive numeric is allowed' })
  @minNumber({ value: 1, message: "Minimum is 1" })
  @maxNumber({ value: 2147483647, message: "Maximum is 2147483647" })
  quantity: number;
  @prop()
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  artistFee: string;
  @prop()
  @required()
  automateDataState: number | null;
  @prop()
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  feeToClient: number;
  @prop()
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  travel: number;
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  @prop()
  accommodation: number;
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  @prop()
  shipping: number;
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  @prop()
  perDiem: number;
  @numeric({ acceptValue: NumericValueType.Both, allowDecimal: true, message: 'Only numeric is allowed' })
  @prop()
  thirdPartyCommission: number;
  total: number;
  @prop()
  @required()
  proposalId: string;
  @prop()
  feeIncludes: string | null;
  @prop()
  feeExcludes: string | null;
  @prop()
  @numeric({ acceptValue: NumericValueType.PositiveNumber, allowDecimal: false, message: 'Only numeric is allowed' })
  order: number;
  @prop()
  priceDescription: string | null;
  @prop()
  feeItems: ProposalArtistLineItem[];
  dateModified: string | null;
  dateCreated: string | null;
  deleted: boolean;
  dateDeleted: string | null;
  dataState: AutomateDataState;
  proposal: SaleOpportunityProfile;
}

export class NormalizedProposalArtists extends ProposalArtists {
  constructor(data?: any) {
    super();
    
    // Priority: camelCase -> PascalCase -> safe default
    // Required fields always have values
    this.id = data?.id ?? data?.Id ?? 0;
    this.profileId = data?.profileId ?? data?.ProfileId ?? '';
    this.quantity = data?.quantity ?? data?.Quantity ?? 1; // Required, min 1
    this.artistFee = data?.artistFee ?? data?.ArtistFee ?? '0';
    this.automateDataState = data?.automateDataState ?? data?.AutomateDataState ?? 0; // Required, default 0 instead of null
    this.proposalId = data?.proposalId ?? data?.ProposalId ?? '';
    
    // Numeric fields - always number, never null
    this.feeToClient = data?.feeToClient ?? data?.FeeToClient ?? 0;
    this.travel = data?.travel ?? data?.Travel ?? 0;
    this.accommodation = data?.accommodation ?? data?.Accommodation ?? 0;
    this.shipping = data?.shipping ?? data?.Shipping ?? 0;
    this.perDiem = data?.perDiem ?? data?.PerDiem ?? 0;
    this.thirdPartyCommission = data?.thirdPartyCommission ?? data?.ThirdPartyCommission ?? 0;
    this.total = data?.total ?? data?.Total ?? 0;
    this.order = data?.order ?? data?.Order ?? 0;
    
    // Nullable fields
    this.feeIncludes = data?.feeIncludes ?? data?.FeeIncludes ?? null;
    this.feeExcludes = data?.feeExcludes ?? data?.FeeExcludes ?? null;
    this.priceDescription = data?.priceDescription ?? data?.PriceDescription ?? null;
    this.feeItems = data?.feeItems ?? data?.FeeItems ?? [];
    this.dateModified = data?.dateModified ?? data?.DateModified ?? null;
    this.dateCreated = data?.dateCreated ?? data?.DateCreated ?? null;
    this.deleted = data?.deleted ?? data?.Deleted ?? false;
    this.dateDeleted = data?.dateDeleted ?? data?.DateDeleted ?? null;
    this.dataState = data?.dataState ?? data?.DataState ?? null;
    this.proposal = data?.proposal ?? data?.Proposal ?? null;
  }
}

export class ComputedProposalArtist extends ProposalArtists {
  @prop()
  get estTotalArtistFee(): number {
    return (this.artistFee ? parseFloat(this.artistFee.toString()) : 0) * (this.quantity ?? 0);
  }
  set estTotalArtistFee(_value: number) {
    // Intentionally no-op: this is a computed field.
  }
  @prop()
  get estTotalClientFee(): number {
    return (this.feeToClient ? parseFloat(this.feeToClient.toString()) : 0) * (this.quantity ?? 0);
  }
  set estTotalClientFee(_value: number) {
    // Intentionally no-op: this is a computed field.
  }
  @prop()
  get itemsCostTotal(): number {
    return (this.feeItems || []).reduce((sum: number, item: any) => sum + (parseFloat(item.cost) || 0), 0) * (this.quantity ?? 1);
  }
  set itemsCostTotal(_value: number) {
    // Intentionally no-op
  }
  @prop()
  get itemsClientFeeTotal(): number {
    return (this.feeItems || []).reduce((sum: number, item: any) => sum + (parseFloat(item.clientFee) || 0), 0) * (this.quantity ?? 1);
  }
  set itemsClientFeeTotal(_value: number) {
    // Intentionally no-op
  }
}

export interface ProposalRevision {
  id: number;
  proposalId: string;
  content: string;
  html: string;
  userId: string;
  isActive: boolean;
  friendlyUrl: string;
  deleted: boolean;
  dateDeleted: string | null;
  dateModified: string | null;
  dateCreated: string | null;
  user: UserModel;
}
export interface ProposalWhiteLabelData {
  rootWhiteLabelUrl: string;
  whiteLabelUrl: string;
  proposalId: string;
}

export class AddProfileVendorViewModel {
  profileId: string;
  vendorId: string;
  profileType: string;
  vendorType: string;
  roles: string[];
  isPrimary: boolean | null;
  replaceExistingSigner?: boolean;
}

export class ClientAndServerTimeModel {
  currentFormatTime: string;
  closedDate: Date | string | null;
}
